import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Input from "../components/Input";
import Checkbox from "../components/Checkbox";
import AuthLayout from "../components/AuthLayout";
import { validateCPF } from "../utils/formatCPF";
import authService from "../services/authService";
import { RegistrationData } from "../models/authModel";
import useMaskedInput from "../hooks/useMaskedInput";

export default function Register() {
  const navigate = useNavigate();
  const [docType, setDocType] = useState("CPF");
  const nameMask = useMaskedInput("", "name");
  const emailMask = useMaskedInput("", "email");
  const phoneMask = useMaskedInput("", "phone");
  const documentMask = useMaskedInput("", "document", { docType });
  const name = nameMask.value;
  const email = emailMask.value;
  const phone = phoneMask.value;
  const document = documentMask.value;
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [docError, setDocError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isFormValid =
    name.trim() !== "" &&
    email.trim() !== "" &&
    phone.trim() !== "" &&
    password.trim().length >= 6 &&
    password === confirmPassword &&
    emailRegex.test(email) &&
    (docType === "CPF" ? validateCPF(document) : document.trim().length > 0) &&
    agreeTerms;

  const handleEmailChange = (e) => {
    emailMask.onChange(e);
    const newEmail = typeof e === 'string' ? e : e.target.value;
    if (newEmail.trim() === "" || emailRegex.test(newEmail)) {
      setEmailError("");
    } else {
      setEmailError(
        "Por favor, insira um e-mail válido (ex: joaosilva@exemplo.com)"
      );
    }
  };

  const handleDocumentChange = (e) => {
    documentMask.onChange(e);
    const rawValue = typeof e === 'string' ? e : e.target.value;
    const cleaned = (documentMask.value || '').replace(/\D/g, '');
    if (docType === "CPF" && rawValue.length > 0 && cleaned.length === 11 && !validateCPF(documentMask.value)) {
      setDocError("CPF inválido ou incompleto.");
    } else {
      setDocError("");
    }
  };

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setValidationErrors([]);

    // Validação usando modelo profissional
    const registration = new RegistrationData({
      name,
      email,
      password,
      confirmPassword,
      document,
      docType,
      phone,
      agreeTerms
    });

    const validation = registration.validate();
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }

    setSubmitting(true);
    try {
      await authService.registerUser(registration.toJSON());
      navigate('/dashboard');
    } catch (err) {
      setError(err?.message || "Falha ao cadastrar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Cadastrar uma conta" widthClass="w-80">
        <form onSubmit={handleRegister} noValidate className="mb-8">
          
          {/* Exibir erros de validação */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4">
              {validationErrors.map((error, index) => (
                <div key={index} className="text-sm flex items-start">
                  <span className="mr-2">•</span>
                  <span>{error}</span>
                </div>
              ))}
            </div>
          )}
          <Input
            label="Nome*"
            placeholder="João Silva"
            value={name}
            onChange={nameMask.onChange}
          />

          <Input
            label="E-mail*"
            type="email"
            placeholder="joaosilva@exemplo.com"
            value={email}
            onChange={handleEmailChange}
          />
          {emailError && (
            <p className="text-sm text-red-500 -mt-3 mb-4">{emailError}</p>
          )}

          <div className="flex gap-2 mb-4">
            <select
              className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            >
              <option>CPF</option>
              <option>CNPJ</option>
            </select>
            <input
              type="text"
              placeholder={
                docType === "CPF" ? "123.456.789-01" : "00.000.000/0000-00"
              }
              className="w-2/3 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={document}
              onChange={handleDocumentChange}
            />
          </div>
          {docError && (
            <p className="text-sm text-red-500 -mt-3 mb-4">{docError}</p>
          )}

          <Input
            label="Telefone*"
            placeholder="(99) 99999-9999"
            value={phone}
            onChange={phoneMask.onChange}
          />

          <Input
            label="Senha*"
            type="password"
            placeholder="Uma senha forte"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Input
            label="Confirme a senha*"
            type="password"
            placeholder="A mesma senha forte"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <div className="mt-3 mb-5">
            <Checkbox className="w-full w-max"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              label={
                <>
                  Li, e estou de acordo com os{" "}
                  
                  <button type="button" className="text-purple-600 underline">
                    Termos de Uso
                  </button>{" "}
                  <div>
                  e a{" "}
                  
                  <button type="button" className="text-purple-600 underline">
                    Política de Privacidade
                  </button>{" "}
                  
                  do aplicativo </div> Geppro Seller.
                  
                </>
              }
            />
          </div>

          {error && (
            <p className="text-xs text-red-600 mb-3">{error}</p>
          )}

          <button
            type="submit"
            disabled={!isFormValid || submitting}
            className={`w-full h-10 rounded-lg transition font-medium ${
              isFormValid && !submitting
                ? "bg-purple-600 text-white hover:bg-purple-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {submitting ? 'Criando conta…' : 'Criar conta'}
          </button>

          <p className="text-sm text-center mt-4 text-gray-600">
            Já tem uma conta?{" "}
            <Link to="/" className="text-purple-600 font-medium">
              Clique aqui
            </Link>
          </p>
        </form>
    </AuthLayout>
  );
}
